import { FC, useMemo } from "react";
import { useGummiwormContext } from "@/context/GummiwormContext";
import { aggregateAssetBalances, formatAssetLabel, formatAssetValue } from "@/lib/marketUtils";
import type { AssetValue } from "@module-bindings/types";
import { TradingPaneState } from "./TradingPaneState";
import { ConnectWalletButton } from "@/components/WalletConnect/ConnectWalletButton";

export const AvailableBalance: FC = () => {
  const { ledgerView, myAccountId, accountsReady } = useGummiwormContext();

  const myAccount = ledgerView.account;

  const fallbackAssets = useMemo<AssetValue[]>(() => {
    if (myAccountId == null) return [];

    const absorbedAssets = Object.values(ledgerView?.deposits)
      .filter((deposit) => deposit.accountId === myAccountId && deposit.absorbedInBlock !== 0n && deposit.rejectedInBlock === 0n)
      .flatMap((deposit) => deposit.value.assets);

    return aggregateAssetBalances(absorbedAssets).map((balance) => ({
      asset: balance.asset,
      value: balance.value,
    }));
  }, [ledgerView?.deposits, myAccountId]);

  const balanceAssets = useMemo(
    () => (myAccount?.uncommittedValue.assets.length ? myAccount.uncommittedValue.assets : fallbackAssets),
    [fallbackAssets, myAccount],
  );

  const balances = useMemo(() => {
    return balanceAssets
      .filter((assetValue: AssetValue) => assetValue.value > 0n)
      .map((assetValue: AssetValue) => ({
        key: `${formatAssetLabel(assetValue.asset)}-${assetValue.value.toString()}`,
        label: formatAssetLabel(assetValue.asset),
        formatted: formatAssetValue(assetValue.asset, assetValue.value),
      }));
  }, [balanceAssets]);
  type BalanceRow = (typeof balances)[number];

  return (
    <section className="sr-panel p-5" aria-label="Available balance">
      <h3 className="text-[11px] font-medium text-sr-neutral-300 uppercase tracking-wider mb-4">
        Available Balance
      </h3>
      {balances.length === 0 ? (
        <TradingPaneState
          compact
          eyebrow="Balances"
          title={myAccountId == null ? "Connect a wallet" : "No deposited balances"}
          message={myAccountId == null
            ? "Connect a wallet to view the assets available for trading."
            : "Deposit funds into the exchange to make balances available here."}
          action={myAccountId == null ? <ConnectWalletButton /> : null}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {balances.map((balance: BalanceRow) => (
            <div key={balance.key} className="flex items-center justify-between">
              <span className="text-sm text-white">{balance.label}</span>
              <span className="text-sm font-medium text-white tabular-nums font-display">
                {balance.formatted}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
